import type { ReactElement } from 'react';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { NeonModalDrawer, NeonButtonFooter } from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import {
  CONFIGURATIONS_ID,
  GRADE_CREDIT_ALERTS_PANEL_TITLE,
  GRADE_CREDIT_ALERTS_DESCRIPTION,
  GRADE_CREDIT_ALERTS_NOTE,
  GRADE_CREDIT_ALERTS_PANEL_CANCEL,
  GRADE_CREDIT_ALERTS_PANEL_SAVE,
  GRADE_CREDIT_ALERTS_PANEL_VALIDATION_MIN_MAX,
  GRADE_CREDIT_ALERTS_GRADES,
  GRADE_CREDIT_ALERTS_PANEL_COL_GRADE,
  GRADE_CREDIT_ALERTS_PANEL_COL_MIN,
  GRADE_CREDIT_ALERTS_PANEL_COL_MAX,
  GRADE_CREDIT_ALERTS_PLACEHOLDER_MIN,
  GRADE_CREDIT_ALERTS_PLACEHOLDER_MAX,
} from '@/constants/configurations-constants';
import type { GradeSetting } from '@/repositories/configurations-repository';
import { sanitizeCreditsFromSource } from '@/utils/sanitize-credits-input';
import { bodyTextStyle, panelValidationErrorStyle } from './configurations.styles';
import { GradeCreditRangesTable } from '@/components/GradeCreditRangesTable/grade-credit-ranges-table';
import type { GradeCreditStringRow } from '@/utils/grade-credit-table';
import {
  parseGradeCreditTableNumber,
  gradesWithCreditOrderErrors,
} from '@/utils/grade-credit-table';

const initialRows: Record<number, GradeCreditStringRow> = GRADE_CREDIT_ALERTS_GRADES.reduce(
  (acc, grade) => ({ ...acc, [grade]: { min: '', max: '' } }),
  {} as Record<number, GradeCreditStringRow>
);

function gradeSettingsToRows(
  settings: GradeSetting[] | undefined
): Record<number, GradeCreditStringRow> {
  const byGrade = new Map(settings?.map((s) => [s.grade, s]) ?? []);
  return GRADE_CREDIT_ALERTS_GRADES.reduce(
    (acc, grade) => {
      const s = byGrade.get(grade);
      acc[grade] = {
        min:
          s?.minCredits !== undefined && s?.minCredits !== null
            ? sanitizeCreditsFromSource(s.minCredits)
            : '',
        max:
          s?.maxCredits !== undefined && s?.maxCredits !== null
            ? sanitizeCreditsFromSource(s.maxCredits)
            : '',
      };
      return acc;
    },
    {} as Record<number, GradeCreditStringRow>
  );
}

function rowsToGradeSettings(rows: Record<number, GradeCreditStringRow>): GradeSetting[] {
  return GRADE_CREDIT_ALERTS_GRADES.filter((grade) => {
    const row = rows[grade] ?? { min: '', max: '' };
    return row.min.trim() !== '' || row.max.trim() !== '';
  }).map((grade) => {
    const row = rows[grade] ?? { min: '', max: '' };
    const min = parseGradeCreditTableNumber(row.min);
    const max = parseGradeCreditTableNumber(row.max);
    return {
      grade,
      ...(min !== null ? { minCredits: min } : {}),
      ...(max !== null ? { maxCredits: max } : {}),
    };
  });
}

export type GradeCreditAlertsPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  initialGradeSettings?: GradeSetting[];
  onSave?: (gradeSettings: GradeSetting[]) => void;
};

export function GradeCreditAlertsPanel({
  isOpen,
  onClose,
  initialGradeSettings,
  onSave,
}: GradeCreditAlertsPanelProps): ReactElement | null {
  const [rows, setRows] = useState<Record<number, GradeCreditStringRow>>(initialRows);

  useEffect(() => {
    if (isOpen) {
      setRows(gradeSettingsToRows(initialGradeSettings));
    }
  }, [isOpen, initialGradeSettings]);

  const updateCell = useCallback((grade: number, field: 'min' | 'max', value: string) => {
    setRows((prev) => ({
      ...prev,
      [grade]: { ...prev[grade], [field]: value },
    }));
  }, []);

  const validationErrors = useMemo(
    () => gradesWithCreditOrderErrors(rows, GRADE_CREDIT_ALERTS_GRADES),
    [rows]
  );

  const hasValidationError = validationErrors.length > 0;

  const handleSave = useCallback(() => {
    if (hasValidationError) {
      return;
    }
    onSave?.(rowsToGradeSettings(rows));
    onClose();
  }, [hasValidationError, rows, onSave, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <NeonModalDrawer
      id={CONFIGURATIONS_ID.GradeCreditAlertsPanel}
      neonDrawerHasClosed={onClose}
      dataSize="medium"
      dataShowCloseButton="true"
      dataShowHeaderDivider="true"
      dataShowFooterDivider="true"
      dataCloseOnBackdropClick="true"
    >
      <div data-slot="drawer-header-title">{GRADE_CREDIT_ALERTS_PANEL_TITLE}</div>

      <div data-slot="drawer-body">
        <p style={{ ...bodyTextStyle, marginBottom: '16px' }}>{GRADE_CREDIT_ALERTS_DESCRIPTION}</p>
        <p style={{ ...bodyTextStyle, marginBottom: '20px' }}>{GRADE_CREDIT_ALERTS_NOTE}</p>

        <GradeCreditRangesTable
          grades={GRADE_CREDIT_ALERTS_GRADES}
          rows={rows}
          onCellChange={updateCell}
          labels={{
            gradeHeader: GRADE_CREDIT_ALERTS_PANEL_COL_GRADE,
            minHeader: GRADE_CREDIT_ALERTS_PANEL_COL_MIN,
            maxHeader: GRADE_CREDIT_ALERTS_PANEL_COL_MAX,
            placeholderMin: GRADE_CREDIT_ALERTS_PLACEHOLDER_MIN,
            placeholderMax: GRADE_CREDIT_ALERTS_PLACEHOLDER_MAX,
          }}
          inputIdPrefix="config-grade-credit-alerts"
        />

        <p
          style={{
            ...panelValidationErrorStyle,
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#ffffff',
            padding: '8px 0 0',
            minHeight: '20px',
            visibility: hasValidationError ? 'visible' : 'hidden',
          }}
          role="alert"
          aria-live="polite"
        >
          {hasValidationError ? GRADE_CREDIT_ALERTS_PANEL_VALIDATION_MIN_MAX : ''}
        </p>
      </div>

      <div data-slot="drawer-button-footer">
        <NeonButtonFooter
          id={`${CONFIGURATIONS_ID.GradeCreditAlertsPanel}-footer`}
          buttons={[
            {
              id: CONFIGURATIONS_ID.GradeCreditAlertsPanelCancel,
              text: GRADE_CREDIT_ALERTS_PANEL_CANCEL,
              type: NeonButtonTypes.Borderless,
              onClick: onClose,
            },
          ]}
          primaryButton={{
            id: CONFIGURATIONS_ID.GradeCreditAlertsPanelSave,
            text: GRADE_CREDIT_ALERTS_PANEL_SAVE,
            type: NeonButtonTypes.Primary,
            disabled: hasValidationError,
            onClick: handleSave,
          }}
        />
      </div>
    </NeonModalDrawer>
  );
}
